import React, { useEffect, useState, useCallback } from "react";
import styles from "./AddProduct.module.css";
import { FaEdit, FaTrash, FaSearch } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import WebService from "../../Services/WebService";
import Paginations from "../../Customs/pagination/Paginations";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import AddCategoriesPanel from "./AddCategoriesPanel";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageLoader from "../../Customs/PageLoader/PageLoader";

interface Category {
  category_id: number;
  category_name: string;
  parent_category_id?: number | null;
  basic_door_price?: number | null;
}

interface Pagination {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}

const PER_PAGE = 10;

const ProductCategories: React.FC = () => {
  const [, setAllCategories] = useState<Category[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);

  // --- Use URL params for state ---
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    (searchParams.get("sort") as "asc" | "desc") || "asc"
  );
  const [showPanel, setShowPanel] = useState(false);
  const [editCategory, setEditCategory] = useState<Category | null>(null);
  const navigate = useNavigate();

  // --- Fetch categories ---
const fetchCategories = useCallback(async () => {
  setLoading(true);
  try {
    const qs = new URLSearchParams();
    if (search.trim()) qs.set("like", search.trim());
    qs.set("limit", String(PER_PAGE));
    qs.set("page_number", String(currentPage));
    qs.set("sort_order", sortOrder); 
    


    const action =
      `/v1/door-management/product/categories` + (qs.toString() ? `?${qs}` : "");

    const res: any = await WebService.getAPI({ action });


    let list: Category[] = [];
    let pageMeta: Pagination | null = null;

    if (Array.isArray(res?.data)) {
      list = res.data;
    } else if (Array.isArray(res?.data?.data)) {
      list = res.data.data;
      pageMeta = res.data.pagination || null;
    } else if (Array.isArray(res)) {
      list = res;
    }

    setCategories(list);
    setAllCategories(list);         
    setPagination(pageMeta);        
  } catch {
    setCategories([]);
    setAllCategories([]);
    setPagination(null);
  } finally {
    setLoading(false);
  }
}, [search, currentPage, sortOrder]);
useEffect(() => {
  // keep URL params in sync
  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (currentPage > 1) params.page = String(currentPage);
  if (sortOrder !== "asc") params.sort = sortOrder;
  setSearchParams(params);
  fetchCategories();
}, [search, currentPage, sortOrder, fetchCategories, setSearchParams]);

  useEffect(() => {
    const paramPage = Number(searchParams.get("page")) || 1;
    if (paramPage !== currentPage) setCurrentPage(paramPage);

    const paramSearch = searchParams.get("search") || "";
    if (paramSearch !== search) setSearch(paramSearch);

    const paramSort = (searchParams.get("sort") as "asc" | "desc") || "asc";
    if (paramSort !== sortOrder) setSortOrder(paramSort);
    // eslint-disable-next-line
  }, [searchParams]);

  const handleAddCategory = () => {
    setEditCategory(null);
    setShowPanel(true);
  };

  const handleEdit = (cat: Category) => {
    setEditCategory(cat);
    setShowPanel(true);
  };

  const handlePanelClose = () => {
    setShowPanel(false);
    setEditCategory(null);
  };

  const handleDelete = async (cat: Category) => {
    const confirm = await Swal.fire({
      title: "Delete Category",
      text: `Are you sure you want to delete "${cat.category_name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      heightAuto: false,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await WebService.deleteAPI({
        action: `/v1/door-management/product/categories/${cat.category_id}`,
      });
      toast.success("Category deleted!");
      fetchCategories();
    } catch (err: any) {
      // toast.error("Failed to delete category");
    }
  };

  return (
    <>
      <div className={styles.wrapper}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 7, marginBottom: 8 }}>
          <span className={styles.pageTitle}>Product Categories </span>
          <div className={styles.buttonRow}>
            <button
              className={styles.bigBtn}
              onClick={() => navigate("/inventory/product-design")}
            >
              Product Inventory
            </button>
            <button className={`${styles.bigBtn} ${styles.active}`}>
              Product Categories
            </button>
            <button
              className={styles.bigBtn}
              onClick={() => navigate("/inventory/wood-qualities")}
            >
              Wood Qualities
            </button>
          </div>
        </div>

        <div className={styles.toolbar}>
          <form className={styles.searchForm} onSubmit={e => e.preventDefault()}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search category name…"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              style={{ textAlign: "center" }}
            />
          </form>
          <div className={styles.filterButtons}>
            <select
              className={styles.dropdown}
              value={sortOrder}
              onChange={e => {
                setSortOrder(e.target.value as "asc" | "desc");
                setCurrentPage(1);
              }}
              style={{ textAlign: "center" }}
            >
              <option value="asc">Sort: Oldest First</option>
              <option value="desc">Sort: Newest First</option>
            </select>
          </div>
          <button className={styles.addBtn} onClick={handleAddCategory} type="button">
            <MdAdd style={{ marginRight: 6 }} /> Add Category
          </button>
        </div>

        <div className={styles.productTable}>
          {loading ? (
            <div style={{ padding: 34 }}>
              <PageLoader />
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>Category Name</th>
                  <th style={{ textAlign: "center" }}>Parent Category ID</th>
                  <th style={{ textAlign: "center" }}>Selling Price</th>
                  <th style={{ textAlign: "center" }} className={styles.actionsHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", color: "#888", padding: "32px 0" }}>
                      No Product Categories Found.
                    </td>
                  </tr>
                ) : (
                  categories.map(cat => (
                    <tr key={cat.category_id}>
                      <td style={{ textAlign: "center" }}>{cat.category_name}</td>
                      <td style={{ textAlign: "center" }}>{cat.parent_category_id ?? "-"}</td>
                      <td style={{ textAlign: "center" }}>
                        {typeof cat.basic_door_price === "number"
                          ? `₹${cat.basic_door_price}`
                          : "—"}
                      </td>
                      <td style={{ textAlign: "center" }} className={styles.actions}>
                        <FaEdit
                          className={styles.editIcon}
                          onClick={() => handleEdit(cat)}
                          style={{ cursor: "pointer" }}
                          title="Edit"
                        />
                        <FaTrash
                          className={styles.deleteIcon}
                          onClick={() => handleDelete(cat)}
                          style={{ color: "#e53935", cursor: "pointer" }}
                          title="Delete"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {pagination?.total_records && (
          <div className={styles.paginationWrapper}>
            <Paginations
              totalItems={pagination.total_records}
              itemsCountPerPage={pagination.page_size}
              changePage={setCurrentPage}
              changeCurrentPage={currentPage}
            />
          </div>
        )}
      </div>

      {showPanel && (
        <AddCategoriesPanel
          onClose={handlePanelClose}
          onSuccess={() => {
            fetchCategories();
            handlePanelClose();
          }}
          editCategory={editCategory ?? undefined}
        />
      )}
    </>
  );
};

export default ProductCategories;
